import { useEffect, useMemo, useRef } from 'react'
import { SentryHelper } from 'utils/sentryHelper'
import { usePlinkoAction, usePlinkoBetListener, usePlinkoGameStatus } from 'views/InhouseGame/Plinko/hooks'
import { PlinkoBet } from 'views/InhouseGame/Plinko/types/Bet'
import { useMatter } from 'views/InhouseGame/hooks/useMatter'
import { canvasWidth, plinkoDistancesX, plinkoSizes } from '../../../config'
import PlinkoGraphicBase from '../modals/Base'
import Particle from '../modals/Particle'
import Ripple from '../modals/Ripple'
import useGameParticles from './useGameParticles'
import useGamePlinkos from './useGamePlinkos'
import useGameWalls from './useGameWalls'

const useConfigGameMatter = ({ row }: { row: number }) => {
  const { Matter, World, engine, Engine, world, updateFPSLockStatus, fps } = useMatter()

  const { isPlaying } = usePlinkoGameStatus()
  const { updatePlinkos, plinkos } = useGamePlinkos(row)
  const { createParticle, particles } = useGameParticles(row)
  const { updateWalls, walls } = useGameWalls(row)
  const { finishBet } = usePlinkoAction()
  const ripplesref = useRef([])

  const isLoaded = useMemo(() => Matter && World && engine && Engine && world, [Matter, World, engine, Engine, world])

  useEffect(() => {
    if (isLoaded) {
      setupItemInWorld(updatePlinkos())
      setupItemInWorld(updateWalls())
      onCollisionListener()
    }
  }, [isLoaded, row])

  useEffect(() => {
    updateFPSLockStatus(isPlaying)
  }, [isPlaying])

  usePlinkoBetListener((bet: PlinkoBet) => {
    if (!bet) return

    if (bet.disabledDisplay) {
      setTimeout(() => {
        finishBet(bet)
      }, 1000)
      return
    }

    const particle = createParticle(bet)
    World.add(world, particle.body)
  })

  const setupItemInWorld = (stateHistory: { new: PlinkoGraphicBase[]; old: PlinkoGraphicBase[] }) => {
    const { new: newState, old: oldState } = stateHistory
    oldState.forEach((item) => {
      World.remove(world, item.body)
    })
    newState.forEach((item) => {
      World.add(world, item.body)
    })
  }

  const onCollisionListener = () => {
    Matter.Events.on(engine, 'collisionStart', (event) => {
      for (let i = 0; i < event.pairs.length; i++) {
        const pair = event.pairs[i]
        const findItem = ripplesref.current.find((item) => item?.id === pair?.id)
        if (findItem) return

        const pairBody = [pair.bodyA, pair.bodyB]
        let pairIndex = -1
        if (pair.bodyA.isStatic && pair.bodyA.label === 'Circle Body') {
          pairIndex = 0
        } else if (pair.bodyB.isStatic && pair.bodyA.label === 'Circle Body') {
          pairIndex = 1
        }

        if (pairIndex >= 0) {
          let flag = 0

          const pairAtIndex = pairBody[pairIndex]
          const positionX = pairAtIndex.position.x
          const positionY = pairAtIndex.position.y

          for (let j = 0; j < ripplesref.current.length; j++) {
            if (
              ripplesref.current[j] &&
              ripplesref.current[j].x === positionX &&
              ripplesref.current[j].y === positionY
            ) {
              ripplesref.current[j].size = plinkoSizes[row]
              flag = 1
            }
          }
          if (flag === 0) {
            ripplesref.current.push(
              new Ripple(Matter, {
                x: positionX,
                y: positionY,
                size: plinkoSizes[row],
                origin: plinkoSizes[row],
                id: pair.id,
              }),
            )
          }
        }
      }
    })
  }

  const onParticleCompleted = (particle: Particle, _row: number) => {
    const start = (canvasWidth - (row + 1) * plinkoDistancesX[row]) / 2
    const resultPosition = (particle.body.position.x - start) / plinkoDistancesX[row]
    const payout = Math.floor(resultPosition)

    if (payout !== particle.bet.payoutIndex) {
      SentryHelper.captureFeatureClientError({
        feature: 'InhouseGame Plinko',
        error: new Error('Plinko Wrong Payout'),
        options: {
          payload: {
            fps,
            position: particle.body.position,
            row: _row,
            bet: particle.bet,
            startX: particle.x,
            payoutWrong: payout,
          },
        },
      })
    }
    World.remove(world, particle.body)
  }

  return { ripples: ripplesref.current, plinkos, particles, walls, onParticleCompleted, isLoaded }
}

export default useConfigGameMatter
